import { useState, useEffect } from 'react';
import { teamAPI, rbacAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Users, Shield, Star, User, BarChart3, Trophy } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const DEFAULT_ROLE_CONFIG = {
  admin: { icon: Shield, color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', label: 'Admin' },
  manager: { icon: Star, color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', label: 'Manager' },
  agent: { icon: User, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', label: 'Agent' },
};

export default function TeamPage() {
  const [members, setMembers] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    const fetch = async () => {
      try {
        const [teamRes, rolesRes] = await Promise.all([
          teamAPI.list(),
          user?.role === 'admin' ? rbacAPI.getRoles().catch(() => ({ data: { default_roles: [], custom_roles: [] } })) : Promise.resolve({ data: { default_roles: [], custom_roles: [] } }),
        ]);
        setMembers(teamRes.data);
        const defaults = (rolesRes.data.default_roles || []).map(r => ({ id: r.id, name: r.name }));
        const custom = (rolesRes.data.custom_roles || []).map(r => ({ id: r.id, name: r.name }));
        setAllRoles([...defaults, ...custom]);
      } catch {} finally { setLoading(false); }
    };
    fetch();
  }, [user]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await teamAPI.updateRole(userId, newRole);
      toast.success('Role updated');
      const roleName = DEFAULT_ROLE_CONFIG[newRole]?.label || allRoles.find(r => r.id === newRole)?.name || newRole;
      setMembers(prev => prev.map(m => m.user_id === userId ? { ...m, role: newRole, role_name: roleName } : m));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const getRoleConfig = (roleId, roleName) => {
    if (DEFAULT_ROLE_CONFIG[roleId]) return DEFAULT_ROLE_CONFIG[roleId];
    return {
      icon: Shield,
      color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      label: roleName || roleId,
    };
  };


  if (loading) return <div className="h-64 flex items-center justify-center"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  if (user?.role === 'agent') {
    return (
      <div className="space-y-5 fade-in" data-testid="team-page">
        <h1 className="text-2xl font-bold tracking-tight">Team</h1>
        <Card className="border-border/60"><CardContent className="p-8 text-center text-muted-foreground">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Team management is available for managers and admins.</p>
        </CardContent></Card>
      </div>
    );
  }

  // Group role counts
  const roleCounts = {};
  members.forEach(m => {
    const label = getRoleConfig(m.role, m.role_name).label;
    roleCounts[label] = (roleCounts[label] || 0) + 1;
  });

  return (
    <div className="space-y-5 fade-in" data-testid="team-page">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Team</h1>
        <p className="text-muted-foreground text-xs sm:text-sm">{members.length} leden</p>
      </div>

      {/* Summary Cards */}
      <div className="flex flex-wrap gap-3">
        {Object.entries(roleCounts).map(([label, count]) => (
          <Card key={label} className="border-border/60">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                <Shield className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-xl font-bold">{count}</p>
                <p className="text-xs text-muted-foreground">{label}{count !== 1 ? 's' : ''}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Members List */}
      <Card className="border-border/60">
        <CardHeader className="pb-3"><CardTitle className="text-sm">All Members</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-3">
            {members.map((member) => {
              const config = getRoleConfig(member.role, member.role_name);
              const Icon = config.icon;
              return (
                <div key={member.user_id} className="flex items-center justify-between py-3 border-b border-border/30 last:border-0" data-testid={`team-member-${member.user_id}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                      {member.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium">{member.name}</p>
                        <Badge variant="secondary" className={`text-[10px] ${config.color}`}>
                          <Icon className="w-3 h-3 mr-1" />{config.label}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right hidden md:block">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1.5">
                          <BarChart3 className="w-3.5 h-3.5 text-muted-foreground" />
                          <span className="text-xs">{member.lead_count || 0} leads</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Trophy className="w-3.5 h-3.5 text-amber-500" />
                          <span className="text-xs">{member.won_count || 0} won</span>
                        </div>
                      </div>
                    </div>
                    {user?.role === 'admin' && member.user_id !== user.user_id && (
                      <select
                        value={member.role}
                        onChange={(e) => handleRoleChange(member.user_id, e.target.value)}
                        data-testid={`role-select-${member.user_id}`}
                        className="flex h-8 w-[150px] rounded-md border border-input bg-background px-2 text-xs shadow-sm"
                      >
                        {allRoles.map(r => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
